import React from "react";
import { useBCMSModal, BCMSRecord, ModalClose, StateManager, useStateManager, BCMSStateManagerCRUD, useBCMSContext } from "bcms-react";
import Classes, { ContactInfo } from "model";

import { LayoutContainer, LeftColumnContainer, RightColumnContainer } from "components/shared";
import UINotifiButton from "components/shared/notifi/UINotifiButton";
import { UITextInput } from "components/ui";
import { SVGAsset } from "res";
import ProgressionBarWidget, { ProgressionStep } from "../ProgressionBarWidget";

import "./styles.css";
import "./mobile.css";

export default function EnterActivationCodeView(props) {
    const data = { ...getDefaultViewData(), ...props.data ?? {} };
    const ui = { ...getDefaultViewUI(), ...props.ui ?? {} };

    const globalController = useBCMSContext();
    const { close } = useBCMSModal();

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onClose: evt => {
            const contactUpdates = state.data.unconfirmedContacts.map(info => {
                if(state.ui.workflowType === "confirmNowWorkflow") {
                    return info.userIsConfirmingContactInfo ? ContactInfo.update(info) : ContactInfo.delete(info);
                }

                return ContactInfo.create(info);
            }, []);

            BCMSRecord.batch(contactUpdates, globalController, Classes);

            close("AddContactMethodModal");

            state.ui.refresh();
        },

        onChange: (id, value) => {
            const unconfirmed = state.data.unconfirmedContacts.map(info => {
                if(info.id === id) { info.userEnteredActivationCode = value; }

                return info;
            }, []);

            BCMSStateManagerCRUD.init(state)
                .update([ "data", "unconfirmedContacts" ], unconfirmed)
                .submit(setState);
        },

        onActivate: id => {
            const unconfirmed = state.data.unconfirmedContacts.map(info => {
                if(info.id === id) { info.isConfirmed = "true"; }

                return info;
            }, []);

            BCMSStateManagerCRUD.init(state)
                .update([ "data", "unconfirmedContacts" ], unconfirmed)
                .submit(setState);
        }
    });

    return (
        <StateManager state={ { data, ui } } controller={ controller }>
            <EnterActivationCodeContent />
        </StateManager>
    );
}

export const getDefaultViewData = () => ({
  unconfirmedContacts: []
});
export const getDefaultViewUI = () => ({
  workflowType: "confirmNowWorkflow"
});

function EnterActivationCodeContent(props) {
    const { data, ui, onClose } = useStateManager();

    const showActivationSent = data.unconfirmedContacts.filter(info => {
        if(ui.workflowType === "confirmNowWorkflow") { return info.isConfirmed === "false" && info.userIsConfirmingContactInfo; }
        return info.isConfirmed === "false";
    }, []).length > 0;
    const showIsActivated = data.unconfirmedContacts.reduce((result, info) => info.isConfirmed === "true" ? true : result, false);

    const activationCodeRows = data.unconfirmedContacts.map(info => {
        if(info.isConfirmed === "true") { return <CodeActivated key={ info.id } contactInfo={ info } /> }
        else if(ui.workflowType === "confirmNowWorkflow") {
            if(info.userIsConfirmingContactInfo === true) {
                return <EnterActivationCode key={ info.id } contactInfo={ info } />
            }
        }
        else { return <EnterActivationCode key={ info.id } contactInfo={ info } />; }
    }, []);

    return (
        <LayoutContainer>
          <LeftColumnContainer>
              <div className="flex-container justify-right close-container mobile-container">
                  <ModalClose id="AddContactMethodModal" className="clickable text-right">
                      <SVGAsset id="CloseX" data-point="Add_Contact_Method_Modal_Close_Button" />
                  </ModalClose>
              </div>
              {/* The progression bar ui is updated here as the user navigates through the workflow. */}
              
              <ProgressionBarWidget>
                  <ProgressionStep completedClass="completed" />
                  <ProgressionStep activeClass={ showIsActivated ? "completed" : "active" } />
              </ProgressionBarWidget>
    
              <div className="enter-activation-code-view">
                <div className="flex-container flex-vertical all-center header-container text-center">
                  <h1>Enter activation code</h1>
                  <h3>Check each contact method and enter the activation code sent.</h3>
                </div>
                { showActivationSent &&
                  <div className="flex-container all-center activation-green-text">
                    <SVGAsset id="InfoIcon" className="icon info-icon" />
                    <p>Activation code(s) sent.</p>
                  </div>
                }
                { activationCodeRows }
                <div className="flex-container justify-right">
                  <UINotifiButton className="clickable" text="Close" onClick={ onClose } dataPoint="Close"/>
                </div>
              </div>
          </LeftColumnContainer>
    
          <RightColumnContainer>
              <div className="flex-container justify-right close-container">
                  <ModalClose id="AddContactMethodModal" className="clickable">
                      <SVGAsset onClick={ onClose } id="CloseX" />
                  </ModalClose>
              </div>
              <h2>What are notifications?</h2>
              <p>Notifications are a great way to stay on top of important things happening with your profile and accounts.</p>
              <p>You can turn on the notifications that matter most to you and personalize them to meet your needs.</p>
              <h2>Tip</h2>
              <div className="flex-container">
                  <div className="set-width">
                      <SVGAsset id="InfoIcon" className="icon info-icon" />
                  </div>
                  <div className="flex-width">
                      <p>You can add or change your contact info later in notifications Settings.</p>
                  </div>
              </div>
          </RightColumnContainer>
        </LayoutContainer>
    )
}

export function EnterActivationCode(props){ 
    const { onChange, onActivate } = useStateManager();
    const contactInfo = props.contactInfo ?? ContactInfo.firstInstanceOf();
    const updateEnterCodeInput = value => onChange(contactInfo.id, value);
    const activateClicked = evt => onActivate(contactInfo.id);

    return (
        <div className="enter-activation-code">
          <LayoutContainer>
            <LeftColumnContainer>
              <div className="flex-container flex-vertical">
                <div className="flex-height">
                  <div className="flex-container contact-method-container align-center">
                  { contactInfo.type === "phone" &&
                    <SVGAsset id="SquareMessageBubbleIcon" className="icon square-message-bubble-icon" />
                  }
                  { contactInfo.type === "email" &&
                    <SVGAsset id="EnvelopeIcon" className="icon square-message-bubble-icon" />
                  }
                  <span>{ contactInfo.value }</span>
                </div>
                </div>
                <div className="flex-height">
                  <UITextInput placeholder="Enter code" value={ contactInfo.userEnteredActivationCode }
               onChange={ updateEnterCodeInput } dataPoint={ `EnterCode_${ contactInfo.id }` }/>
                </div>
              </div>
            </LeftColumnContainer>
            <RightColumnContainer>
              <div className="flex-container flex-vertical">
                <div className="flex-height">
                  {/* for spacing */}
                </div>
                <div className="flex-height">
                  {/* If user entered an activation code, so show this. */}
                  { (contactInfo.userEnteredActivationCode === undefined
                  || contactInfo.userEnteredActivationCode === "") && 
                    <UINotifiButton className="clickable action-button" text="Resend code" dataPoint={ `Resend_Code_Button_${ contactInfo.id }` }/>
                  }
                  {/* Else user did not enter an activation code, so show this. */}
                  { (contactInfo.userEnteredActivationCode !== undefined
                  && contactInfo.userEnteredActivationCode !== "") && 
                    <UINotifiButton className="clickable" text="Activate" onClick={ activateClicked } dataPoint={ `Activate_${ contactInfo.id }` }/>
                  }
                </div>
              </div>
            </RightColumnContainer>
          </LayoutContainer>
        </div>
    );
}

export function CodeActivated(props) {
    const contactInfo = props.contactInfo ?? "[contactInfo]";
    const successDataPoint = `Success_${ contactInfo.id }`;
  
    return (
      <div className="code-activated">
        <div className="flex-container flex-horizontal align-center layout-container">
          <div className="flex-container flex-vertical set-width left-column">
            <div className="flex-container contact-method-container align-center">
              { contactInfo.type === "phone" &&
                <SVGAsset id="SquareMessageBubbleIcon" className="icon square-message-bubble-icon" />
              }
              { contactInfo.type === "email" &&
                <SVGAsset id="EnvelopeIcon" className="icon square-message-bubble-icon" />
              }
                <span>{ contactInfo.value }</span>
            </div>
          </div>
          <div className="flex-container flex-vertical flex-width right-column">
            <div className="flex-container activation-green-text align-center">
                <SVGAsset id="CheckmarkBadgeIcon" className="checkmark-badge-icon inline-text-icon left-icon" />
                <p data-point={ successDataPoint }>Activated.</p>
            </div>
          </div>
        </div>
      </div>
  
    );
  }